import { union } from 'lodash-es';
import { DateTime } from 'luxon';
import type { HourTimestamp } from 'types/hour';
import type { Duration } from 'types/units';
import debug from 'utils/debug';
import { calculateDuration } from 'utils/durations';
import { fromISO, fromISOWithZone } from 'utils/luxon-helpers';
import { roundToNearest } from './numbers';

export function adjustTimestampsToDuration(
  timestamps: Array<HourTimestamp>,
  newDurationInSeconds: number
): Array<HourTimestamp> {
  const currentDuration = calculateDurationFromTimestamps(timestamps);
  const difference = newDurationInSeconds - currentDuration.total_seconds;

  if (timestamps.length === 0 || difference === 0) {
    return timestamps;
  } else if (difference > 1) {
    return incrementLastTimestamp(timestamps, difference);
  } else {
    return shrinkTimestamps(timestamps, difference * -1);
  }
}

function incrementLastTimestamp(
  base: ReadonlyArray<HourTimestamp>,
  seconds: number
): Array<HourTimestamp> {
  const timestamps = [...base];

  const lastTimestamp = timestamps.pop()!;
  const lastTo = fromISOWithZone(lastTimestamp.to);
  const extendedTo = lastTo.plus({ seconds }).toISO();

  const extendedTimestamp = {
    ...lastTimestamp,
    to: extendedTo,
  };

  timestamps.push(extendedTimestamp);
  return timestamps;
}

function shrinkTimestamps(
  base: ReadonlyArray<HourTimestamp>,
  seconds: number
): Array<HourTimestamp> {
  let timestamps = [...base];
  let remaining = seconds;

  while (remaining > 0) {
    const lastTimestamp = timestamps[timestamps.length - 1];
    const lastFrom = fromISO(lastTimestamp.from);
    const lastTo = fromISO(lastTimestamp.to);
    const lastDuration = lastTo.diff(lastFrom, 'seconds').seconds;

    if (remaining > lastDuration) {
      timestamps.pop();
      remaining -= lastDuration;
    } else {
      timestamps = incrementLastTimestamp(timestamps, -remaining);
      remaining = 0;
    }
  }

  return timestamps;
}

export function calculateDurationFromTimestamps(timestamps: Array<HourTimestamp>): Duration {
  const durations = timestamps.map(timestamp => {
    const from = fromISO(timestamp.from);
    const to = fromISO(timestamp.to);
    return to.diff(from, 'seconds').seconds;
  });

  const total = durations.reduce((total, current) => total + current, 0);
  return calculateDuration(total);
}

/**
 * Merges overlapping timestamps into a single timestamp, while
 * preserving the entry_ids of the merged timestamps.
 */
export function mergeOverlappingTimestampsContainingTicMemories(
  base: ReadonlyArray<HourTimestamp>,
  offset = 0
): Array<HourTimestamp> {
  const timestamps = [...base];

  compareUntilAllSolved(timestamps, (timestampA, timestampB, indexA, indexB) => {
    const merge = mergeTimestampsIfOverlapping(timestampA, timestampB, offset);

    if (merge !== null) {
      updateArrayWithMerge(timestamps, merge, indexA, indexB);
      return true;
    }

    return false;
  });

  return timestamps.sort((a, b) => new Date(a.from).getTime() - new Date(b.from).getTime());
}

function compareUntilAllSolved<T>(
  entries: Array<T>,
  modifier: (a: T, b: T, indexA: number, indexB: number) => boolean
) {
  let hasChanges = true;

  while (hasChanges) {
    hasChanges = entries.some((a, indexA) =>
      entries.some((b, indexB) => {
        if (indexA !== indexB) {
          return modifier(a, b, indexA, indexB);
        } else {
          return false;
        }
      })
    );
  }
}

function mergeTimestampsIfOverlapping(
  first: HourTimestamp,
  second: HourTimestamp,
  offset: number
): Partial<HourTimestamp> | null {
  const startA = fromISO(first.from).startOf('minute').toSeconds();
  const endA = fromISO(first.to).startOf('minute').toSeconds();
  const startB = fromISO(second.from).startOf('minute').toSeconds();
  const endB = fromISO(second.to).startOf('minute').toSeconds();

  const latestStart = Math.max(startA - offset, startB - offset);
  const earliestEnd = Math.min(endA + offset, endB + offset);
  const hasOverlap = latestStart <= earliestEnd;

  if (hasOverlap) {
    const from = startA <= startB ? first.from : second.from;
    const to = endA >= endB ? first.to : second.to;
    return { from, to };
  } else {
    return null;
  }
}

function updateArrayWithMerge(
  timestamps: Array<HourTimestamp>,
  changes: Partial<HourTimestamp>,
  atIndexA: number,
  atIndexB: number
) {
  let indexToPreserve = atIndexA;
  let indexToRemove = atIndexB;

  const shouldPreserveIndexB = !timestamps[atIndexA]?.id && !!timestamps[atIndexB]?.id;
  if (shouldPreserveIndexB) {
    indexToPreserve = atIndexB;
    indexToRemove = atIndexA;
  }

  const original = timestamps[indexToPreserve];
  const lost = timestamps[indexToRemove];
  const mergedEntryIds = union(original.entry_ids, lost.entry_ids);

  timestamps[indexToPreserve] = { ...original, ...changes, entry_ids: mergedEntryIds };
  timestamps.splice(indexToRemove, 1);
}

/**
 * Find the earliest `from`, and latest `to` from a list of timestamps.
 * Useful when setting timestamps from "virtual" groups made of many timestamps.
 */
export function getSingleFromToViaTimestamps(timestamps: HourTimestamp[]): {
  from: string;
  to: string;
} {
  let earliestFrom = fromISO(timestamps[0].from);
  let latestTo = fromISO(timestamps[0].to);

  timestamps.forEach(timestamp => {
    const timestampFrom = fromISO(timestamp.from);
    const timestampTo = fromISO(timestamp.to);

    if (timestampFrom < earliestFrom) {
      earliestFrom = timestampFrom;
    }

    if (timestampTo > latestTo) {
      latestTo = timestampTo;
    }
  });

  return { from: earliestFrom.toISO(), to: latestTo.toISO() };
}

/**
 * Mutates the range of timestamps by either adding or removing a specified range.
 *
 * @param action - 'add' or 'remove'
 * @param rangeMutation - The range(s) to be added or removed.
 * @param existingRanges - The array of existing timestamp ranges.
 * @returns An array of modified timestamp ranges.
 */
export function mutateTimestampRange(
  action: 'add' | 'remove',
  rangeMutation: HourTimestamp | HourTimestamp[],
  existingRanges: HourTimestamp[] = []
): HourTimestamp[] {
  const rangeMutations = Array.isArray(rangeMutation) ? rangeMutation : [rangeMutation];

  // We'll round the timestamps to full minutes to avoid any <1m Memories and _tiny_ selections
  // This makes it technically less correct, but nobody cares about per-second accuracy.
  const sortedRangeMutations = rangeMutations
    .map(range => {
      const from = fromISO(range.from).startOf('minute');
      const to = fromISO(range.to);
      const finalTo =
        to.second === 0 && to.diff(from, 'minutes').minutes >= 1
          ? to
          : to.plus({ minute: 1 }).startOf('minute');
      return { ...range, from: from.toISO(), to: finalTo.toISO() };
    })
    .sort((a, b) => fromISO(a.from).toMillis() - fromISO(b.from).toMillis());

  const sortedExistingRanges = [...existingRanges].sort((a, b) => {
    return fromISO(a.from).toMillis() - fromISO(b.from).toMillis();
  });

  debug.template.hour('timestamps::mutateTimestampRange', {
    action,
    rangeMutation,
    existingRanges,
    expand: true,
  });

  switch (action) {
    case 'add':
      return sortedRangeMutations.reduce(
        (acc, range) => addRangeToExisting(range, acc),
        sortedExistingRanges
      );
    case 'remove':
      return sortedRangeMutations.reduce(
        (acc, range) => removeRangeFromExisting(range, acc),
        sortedExistingRanges
      );
    default:
      throw new Error('Invalid action');
  }
}

function addRangeToExisting(
  rangeToAdd: HourTimestamp,
  existingRanges: HourTimestamp[]
): HourTimestamp[] {
  const combinedRanges: HourTimestamp[] = [];
  let expandedFrom: DateTime<true> = fromISO(rangeToAdd.from);
  let expandedTo: DateTime<true> = fromISO(rangeToAdd.to);
  let mergedEntryIds: Array<string | number> = rangeToAdd.entry_ids || [];

  existingRanges.forEach(existingRange => {
    const existingFrom = fromISO(existingRange.from);
    const existingTo = fromISO(existingRange.to);

    // Merge with existing range if overlapping
    if (existingTo >= expandedFrom && existingFrom <= expandedTo) {
      expandedFrom = DateTime.min(expandedFrom, existingFrom) as DateTime<true>;
      expandedTo = DateTime.max(expandedTo, existingTo) as DateTime<true>;
      // Merge entry_ids from overlapping ranges
      mergedEntryIds = [...mergedEntryIds, ...(existingRange.entry_ids || [])];
    } else {
      combinedRanges.push(existingRange);
    }
  });

  // Add the expanded range with merged entry_ids
  combinedRanges.push({
    ...rangeToAdd,
    from: expandedFrom.toISO(),
    to: expandedTo.toISO(),
    entry_ids: [...new Set(mergedEntryIds)], // Remove duplicates
  });

  // Sort by start time for consistency
  return combinedRanges.sort((a, b) => fromISO(a.from).toMillis() - fromISO(b.from).toMillis());
}

function removeRangeFromExisting(
  rangeToRemove: HourTimestamp,
  existingRanges: HourTimestamp[]
): HourTimestamp[] {
  return existingRanges.reduce((adjustedRanges, currentRange) => {
    const currentFrom = fromISO(currentRange.from);
    const currentTo = fromISO(currentRange.to);
    const removeFrom = fromISO(rangeToRemove.from);
    const removeTo = fromISO(rangeToRemove.to);

    // Check and adjust overlapping ranges
    if (currentTo > removeFrom && currentFrom < removeTo) {
      if (currentFrom < removeFrom) {
        adjustedRanges.push({ ...currentRange, from: currentFrom.toISO(), to: removeFrom.toISO() });
      }
      if (currentTo > removeTo) {
        adjustedRanges.push({ ...currentRange, from: removeTo.toISO(), to: currentTo.toISO() });
      }
    } else {
      adjustedRanges.push(currentRange);
    }

    return adjustedRanges;
  }, [] as HourTimestamp[]);
}

export function snapTimestampToNearest(timestamp: string, snapToMinutes: number): string {
  return snapDatetimeToNearest(fromISO(timestamp), snapToMinutes).toISO();
}

export function snapDatetimeToNearest(
  datetime: DateTime<true>,
  snapToMinutes: number
): DateTime<true> {
  const startOfDay = datetime.startOf('day');
  const minutesFromStartOfDay = datetime.diff(startOfDay, 'minutes').minutes;
  const roundedFrom = roundToNearest(minutesFromStartOfDay, snapToMinutes);
  return startOfDay.plus({ minutes: roundedFrom });
}

export function findEarliestTimestamp(timestamps: HourTimestamp[]): HourTimestamp | null {
  if (timestamps.length === 0) {
    return null;
  }
  return timestamps.reduce((earliest, timestamp) => {
    return fromISO(timestamp.from).toMillis() < fromISO(earliest.from).toMillis()
      ? timestamp
      : earliest;
  }, timestamps[0]);
}

export function findLatestTimestamp(timestamps: HourTimestamp[]): HourTimestamp | null {
  if (timestamps.length === 0) {
    return null;
  }
  return timestamps.reduce((latest, timestamp) => {
    return fromISO(timestamp.to).toMillis() > fromISO(latest.to).toMillis() ? timestamp : latest;
  }, timestamps[0]);
}

/**
 * Calculates the total duration of memories with overlaps merged.
 * This excludes idle gaps and avoids double-counting overlapping time intervals.
 *
 * @param memories Array of memory objects with started_at and ended_at or total_seconds
 * @param rangeStart Optional start time to truncate intervals (ISO string)
 * @param rangeEnd Optional end time to truncate intervals (ISO string)
 * @returns Total duration in seconds with overlaps merged
 */
export function calculateNonOverlappingMemoryDuration(
  memories: Array<{ started_at: string; ended_at: string; total_seconds?: number }>,
  rangeStart?: string,
  rangeEnd?: string
): number {
  if (memories.length === 0) {
    return 0;
  }

  // Convert memories to intervals with DateTime objects for easier manipulation
  const intervals = memories
    .map(memory => ({
      start: DateTime.fromISO(memory.started_at),
      end: DateTime.fromISO(memory.ended_at),
    }))
    .filter(interval => interval.start.isValid && interval.end.isValid)
    .filter(interval => interval.start < interval.end); // Filter out invalid intervals

  if (intervals.length === 0) {
    return 0;
  }

  // Apply range boundaries if provided
  const boundedIntervals = intervals
    .map(interval => {
      let start = interval.start;
      let end = interval.end;

      if (rangeStart) {
        const rangeStartTime = DateTime.fromISO(rangeStart);
        if (rangeStartTime.isValid) {
          start = DateTime.max(start, rangeStartTime);
        }
      }

      if (rangeEnd) {
        const rangeEndTime = DateTime.fromISO(rangeEnd);
        if (rangeEndTime.isValid) {
          end = DateTime.min(end, rangeEndTime);
        }
      }

      return { start, end };
    })
    .filter(interval => interval.start < interval.end); // Remove intervals that are completely outside bounds

  if (boundedIntervals.length === 0) {
    return 0;
  }

  // Sort intervals by start time
  const sortedIntervals = boundedIntervals.sort((a, b) => a.start.toMillis() - b.start.toMillis());

  // Merge overlapping intervals
  const mergedIntervals = [];
  let currentInterval = sortedIntervals[0];

  for (let i = 1; i < sortedIntervals.length; i++) {
    const nextInterval = sortedIntervals[i];

    // Check if intervals overlap or are adjacent
    if (currentInterval.end >= nextInterval.start) {
      // Merge intervals by extending the end time
      currentInterval = {
        start: currentInterval.start,
        end: DateTime.max(currentInterval.end, nextInterval.end),
      };
    } else {
      // No overlap, add current interval and move to next
      mergedIntervals.push(currentInterval);
      currentInterval = nextInterval;
    }
  }

  // Add the last interval
  mergedIntervals.push(currentInterval);

  // Calculate total duration of merged intervals
  const totalSeconds = mergedIntervals.reduce((total, interval) => {
    return total + interval.end.diff(interval.start, 'seconds').seconds;
  }, 0);

  return Math.round(totalSeconds);
}
